import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Search, Download, Eye, X } from 'lucide-react';
import { Skeleton } from '../../../skeletons';
import { toast } from 'react-hot-toast';
import residencesService from '../../../services/carehome_manager/residencesService';
import AddIncidentModal from './AddIncidentModal';
import frontBodyAsset from '../../../assets/body_map/body-map-front.svg';
import backBodyAsset from '../../../assets/body_map/body-map-back.svg';
import { Modal, Select } from '../../../components';

const MARKER_SEVERITY_COLOR = { Mild: '#22C55E', Moderate: '#F97316', Severe: '#EF4444' };

// Normalise a marker's position into the 240 x 660 figure space. Dashboard
// marks carry pinX/pinY in that space; Flutter marks carry x/y as 0..1
// fractions — support both so any incident's marks render on the figure.
const markerPoint = (m) => {
    const cx = m.pinX != null ? Number(m.pinX) : (m.x != null ? Number(m.x) * 240 : null);
    const cy = m.pinY != null ? Number(m.pinY) : (m.y != null ? Number(m.y) * 660 : null);
    return (cx == null || cy == null || Number.isNaN(cx) || Number.isNaN(cy)) ? null : { cx, cy };
};

// Read-only body figure for the report view: the real SVG silhouette (nothing
// hidden) with every recorded mark pinned in its severity colour.
function ReportBodyFigure({ side, markers }) {
    const baseImg = side === 'front' ? frontBodyAsset : backBodyAsset;
    const sideMarkers = markers.filter(m => (m.side || 'front') === side);
    return (
        <svg viewBox="0 0 240 660" xmlns="http://www.w3.org/2000/svg" className="w-full" style={{ display: 'block' }}>
            <image href={baseImg} x="0" y="0" width="240" height="660" preserveAspectRatio="xMidYMid meet" />
            {sideMarkers.map(m => {
                const pt = markerPoint(m);
                if (!pt) return null;
                const col = MARKER_SEVERITY_COLOR[m.severity] || '#22C55E';
                return (
                    <g key={m.globalIdx} transform={`translate(${pt.cx},${pt.cy})`}>
                        <circle r="12" fill={col} opacity="0.22" />
                        <circle r="8.5" fill={col} stroke="#fff" strokeWidth="2.5" />
                        <text y="3.3" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff"
                            style={{ userSelect: 'none' }}>{m.globalIdx + 1}</text>
                    </g>
                );
            })}
        </svg>
    );
}

const CATEGORY_LABELS = {
    witnessed: 'Witnessed',
    unwitnessed: 'Unwitnessed',
};

const severityStyles = {
    Severe: 'bg-red-100 text-red-700 dark:bg-red-900/10 dark:text-red-400',
    Moderate: 'bg-orange-100 text-orange-700 dark:bg-orange-900/10 dark:text-orange-400',
    Mild: 'bg-green-100 text-green-700 dark:bg-emerald-900/10 dark:text-emerald-400',
};

// `focusId` (optional) — an incident to open once loaded (reminder link).
const AccidentIncidentTab = ({ focusId = null } = {}) => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [data, setData] = useState({
        records: [],
        summary: { witnessed: 0, unwitnessed: 0 },
        reportData: Array(24).fill(0)
    });

    // Filters
    const [search, setSearch] = useState('');
    const [severityFilter, setSeverityFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all'); // driven by donut + dropdown
    const [hourFilter, setHourFilter] = useState(null);           // driven by frequency chart

    // Details view
    const [selected, setSelected] = useState(null);
    // A reminder link (?focus=<incident id>) opens that incident's details
    // once the records have loaded — see utils/reminderLinks.js. Once per id,
    // so closing the details doesn't reopen them.
    const openedFocus = useRef(null);
    useEffect(() => {
        if (!focusId || openedFocus.current === String(focusId)) return;
        const hit = (data.records || []).find((r) => String(r.id) === String(focusId));
        if (hit) {
            openedFocus.current = String(focusId);
            setSelected(hit);
        }
    }, [focusId, data.records]);

    useEffect(() => {
        const fetchIncidentData = async () => {
            try {
                setLoading(true);
                const response = await residencesService.getResidentIncidents(id);
                setData(response.data);
            } catch (err) {
                console.error('Error fetching incident data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchIncidentData();
        }
    }, [id]);

    const handleAddIncident = async (formData) => {
        await residencesService.createIncident(id, formData);
        toast.success('Incident report submitted successfully');
        const response = await residencesService.getResidentIncidents(id);
        setData(response.data);
    };

    const filteredRecords = useMemo(() => {
        return (data.records || []).filter((r) => {
            if (severityFilter !== 'all' && (r.severity || 'Mild') !== severityFilter) return false;
            if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
            if (hourFilter !== null && r.hour !== hourFilter) return false;
            if (search.trim()) {
                const q = search.trim().toLowerCase();
                const hay = `${r.reference} ${r.cause} ${r.completedBy} ${r.location} ${r.reportedBy}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [data.records, severityFilter, categoryFilter, hourFilter, search]);

    const hasActiveFilters = search.trim() || severityFilter !== 'all' || categoryFilter !== 'all' || hourFilter !== null;

    const clearFilters = () => {
        setSearch('');
        setSeverityFilter('all');
        setCategoryFilter('all');
        setHourFilter(null);
    };

    const toggleCategory = (cat) => setCategoryFilter((prev) => (prev === cat ? 'all' : cat));

    const handleExport = () => {
        if (!filteredRecords.length) {
            toast.error('No records to export');
            return;
        }
        const headers = [
            'Reference', 'Date', 'Time', 'Incident Type', 'Severity', 'Category',
            'Witnessed', 'Location', 'Reported By', 'Logged By', 'Pain Score',
            'Emotion', 'Hit Head', 'Bleeding', 'Medication Given', 'Follow Up Date', 'Description'
        ];
        const esc = (v) => {
            const s = v === null || v === undefined ? '' : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = filteredRecords.map((r) => [
            r.reference, r.date, r.time || '', r.cause, r.severity, CATEGORY_LABELS[r.category] || r.category,
            r.witnessed ? 'Yes' : 'No', r.location, r.reportedBy, r.completedBy, r.painScore,
            r.emotion, r.hitHead ? 'Yes' : 'No', r.signsOfBleeding ? 'Yes' : 'No',
            r.medicationAdministered ? 'Yes' : 'No', r.followUpDate || '', r.description
        ].map(esc).join(','));
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `incidents-${id}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${filteredRecords.length} record${filteredRecords.length === 1 ? '' : 's'}`);
    };

    // Custom SVG Donut Chart — two segments (Witnessed / Unwitnessed), clickable
    // to filter the records table.
    const IncidentSummaryChart = () => {
        const { witnessed, unwitnessed } = data.summary;

        const radius = 45;
        const circumference = 2 * Math.PI * radius;

        const witnessedDash = (witnessed / 100) * circumference;
        const unwitnessedDash = (unwitnessed / 100) * circumference;

        const segProps = (cat) => ({
            onClick: () => toggleCategory(cat),
            style: { cursor: 'pointer' },
            opacity: categoryFilter === 'all' || categoryFilter === cat ? 1 : 0.3,
        });

        return (
            <div className="w-full h-[220px] flex items-center justify-center">
                <svg viewBox="-25 25 320 150" className="w-full h-full max-w-[400px] drop-shadow-sm">
                    {/* Witnessed — label sits on the outer (left) side of the leader dot */}
                    <g {...segProps('witnessed')}>
                        <polyline points="115,90 85,90 70,75 35,75" fill="none" stroke="#3B82F6" strokeWidth="1" />
                        <circle cx="35" cy="75" r="2" fill="#3B82F6" />
                        <text x="30" y="65" fontSize="11" fill="#3B82F6" textAnchor="end" fontWeight="bold">{witnessed}%</text>
                        <text x="30" y="88" fontSize="11" fill="#3B82F6" textAnchor="end">Witnessed</text>
                    </g>

                    {/* Unwitnessed */}
                    <g {...segProps('unwitnessed')}>
                        <polyline points="200,135 220,135 240,155 285,155" fill="none" stroke="#F97316" strokeWidth="1" />
                        <circle cx="285" cy="155" r="2" fill="#F97316" />
                        <text x="285" y="145" fontSize="11" fill="#F97316" textAnchor="end" fontWeight="bold">{unwitnessed}%</text>
                        <text x="285" y="168" fontSize="11" fill="#F97316" textAnchor="end">Unwitnessed</text>
                    </g>

                    {/* Donut Segments */}
                    <circle cx="160" cy="100" r={radius} fill="none" stroke="#3B82F6" strokeWidth="22"
                        strokeDasharray={`${witnessedDash} ${circumference}`} transform="rotate(-90 160 100)" {...segProps('witnessed')} />

                    <circle cx="160" cy="100" r={radius} fill="none" stroke="#F97316" strokeWidth="22"
                        strokeDasharray={`${unwitnessedDash} ${circumference}`} transform={`rotate(${-90 + (witnessed * 3.6)} 160 100)`} {...segProps('unwitnessed')} />

                    <text x="160" y="96" textAnchor="middle" fontSize="13" fill="#6B7280" fontWeight="500">A & I</text>
                    <text x="160" y="114" textAnchor="middle" fontSize="13" fill="#6B7280" fontWeight="500">Summary</text>
                </svg>
            </div>
        );
    };

    // Frequency chart — clicking an hour band filters the records table by that hour
    const IncidentReportChart = () => {
        const maxValue = Math.max(...data.reportData, 5);
        const bandWidth = 1000 / 24;

        const points = data.reportData.map((val, i) => {
            const x = (i / 23) * 1000;
            const y = 200 - (val / maxValue) * 200;
            return `${x},${y}`;
        });

        const path = `M ${points.join(' L ')}`;
        const areaPath = `${path} L 1000,200 L 0,200 Z`;

        return (
            <div className="w-full h-[220px] flex relative mt-2">
                <div className="w-[35px] flex flex-col justify-between text-[11px] font-semibold text-gray-800 dark:text-gray-400 pb-[36px] shrink-0 pt-2 text-right pr-2">
                    <span>{maxValue}</span>
                    <span>{Math.round(maxValue / 2)}</span>
                    <span>0</span>
                </div>

                <div className="flex-1 relative h-[200px]">
                    <div className="absolute inset-0 bottom-[36px]">
                        <svg viewBox="0 0 1000 200" className="w-full h-full" preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                                    <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <line x1="0" y1="0" x2="1000" y2="0" stroke="#f1f5f9" className="dark:stroke-[#31373f]" strokeWidth="2" />
                            <line x1="0" y1="100" x2="1000" y2="100" stroke="#f1f5f9" className="dark:stroke-[#31373f]" strokeWidth="2" />
                            <line x1="0" y1="200" x2="1000" y2="200" stroke="#f1f5f9" className="dark:stroke-[#31373f]" strokeWidth="2" />

                            {/* Highlight selected hour band */}
                            {hourFilter !== null && (
                                <rect x={hourFilter * bandWidth} y="0" width={bandWidth} height="200" fill="#3B82F6" opacity="0.12" />
                            )}

                            <path d={areaPath} fill="url(#blueGradient)" />
                            <path d={path} fill="none" stroke="#3B82F6" strokeWidth="3" />

                            {/* Clickable per-hour bands */}
                            {data.reportData.map((val, i) => (
                                <rect
                                    key={i}
                                    x={i * bandWidth}
                                    y="0"
                                    width={bandWidth}
                                    height="200"
                                    fill="transparent"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setHourFilter((prev) => (prev === i ? null : i))}
                                >
                                    <title>{`${String(i).padStart(2, '0')}:00 — ${val} incident${val === 1 ? '' : 's'}`}</title>
                                </rect>
                            ))}
                        </svg>
                    </div>
                    <div className="absolute bottom-0 w-full h-[36px] flex justify-between text-[11px] font-semibold text-gray-800 dark:text-gray-400">
                        <span>00:00</span>
                        <span>06:00</span>
                        <span>12:00</span>
                        <span>18:00</span>
                        <span>23:00</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
        <AddIncidentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleAddIncident} />
        {selected && <IncidentDetailsModal record={selected} onClose={() => setSelected(null)} />}
        <div className="mt-6 animate-in fade-in duration-300 w-full">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-[18px] font-semibold text-gray-800 dark:text-gray-100">Accident & Incident Monitoring</h2>
                <button
                    onClick={() => setModalOpen(true)}
                    className="bg-[#2563EB] hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition shadow-sm"
                >
                    Add New Incident
                </button>
            </div>

            <div className="flex flex-col gap-8 w-full">
                <div className="flex flex-col lg:flex-row gap-6 w-full">
                    <div className="w-full lg:w-[35%] bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm p-6">
                        <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-200 mb-2">A&I Witness Summary</h3>
                        {loading ? <Skeleton className="h-[220px] w-full rounded-xl" /> : <IncidentSummaryChart />}
                    </div>

                    <div className="w-full lg:w-[65%] bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm p-6">
                        <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-200 mb-2">Incident Frequency (24h)</h3>
                        {loading ? <Skeleton className="h-[220px] w-full rounded-xl" /> : <IncidentReportChart />}
                    </div>
                </div>

                <div>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                        <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100 italic">Recent accident and incident records</h3>

                        {/* Filter + Export toolbar */}
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search ref, type, location…"
                                    className="pl-8 pr-3 py-2 text-[13px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#262b33] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 w-[210px]"
                                />
                            </div>

                            <Select
                                value={severityFilter}
                                onChange={(v) => setSeverityFilter(v)}
                                options={[
                                    { value: 'all', label: 'All severities' },
                                    { value: 'Mild', label: 'Mild' },
                                    { value: 'Moderate', label: 'Moderate' },
                                    { value: 'Severe', label: 'Severe' },
                                ]}
                                className="w-[170px]"
                            />

                            <Select
                                value={categoryFilter}
                                onChange={(v) => setCategoryFilter(v)}
                                options={[
                                    { value: 'all', label: 'All categories' },
                                    { value: 'witnessed', label: 'Witnessed' },
                                    { value: 'unwitnessed', label: 'Unwitnessed' },
                                ]}
                                className="w-[170px]"
                            />

                            <button
                                onClick={handleExport}
                                className="flex items-center gap-1.5 py-2 px-3 text-[13px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#262b33] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#2c313a] transition"
                            >
                                <Download className="w-4 h-4" /> Export
                            </button>
                        </div>
                    </div>

                    {/* Active filter chips */}
                    {hasActiveFilters && (
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            {categoryFilter !== 'all' && (
                                <Chip label={`Category: ${CATEGORY_LABELS[categoryFilter]}`} onClear={() => setCategoryFilter('all')} />
                            )}
                            {hourFilter !== null && (
                                <Chip label={`Time: ${String(hourFilter).padStart(2, '0')}:00`} onClear={() => setHourFilter(null)} />
                            )}
                            {severityFilter !== 'all' && (
                                <Chip label={`Severity: ${severityFilter}`} onClear={() => setSeverityFilter('all')} />
                            )}
                            {search.trim() && (
                                <Chip label={`Search: "${search.trim()}"`} onClear={() => setSearch('')} />
                            )}
                            <button onClick={clearFilters} className="text-[12px] font-medium text-blue-600 hover:underline ml-1">Clear all</button>
                            <span className="text-[12px] text-gray-400 dark:text-gray-500 ml-auto">{filteredRecords.length} of {data.records.length}</span>
                        </div>
                    )}

                    <div className="grid grid-cols-6 px-6 py-2 text-[13px] font-medium text-gray-500 dark:text-gray-400 mb-2 text-center">
                        <div className="text-left">Date</div>
                        <div>Incident Type / Cause</div>
                        <div>Severity</div>
                        <div>A&I Reference</div>
                        <div>Logged By</div>
                        <div className="text-right">Details</div>
                    </div>

                    <div className="flex flex-col gap-3">
                        {loading ? (
                            [1, 2, 3].map(i => <div key={i} className="h-16 bg-gray-50 dark:bg-[#262b33] animate-pulse rounded-xl" />)
                        ) : filteredRecords.length > 0 ? (
                            filteredRecords.map((row) => {
                                const severityStyle = severityStyles[row.severity] || 'bg-gray-100 text-gray-600 dark:bg-[#31373f] dark:text-gray-400';
                                return (
                                <div
                                    key={row.id}
                                    onClick={() => setSelected(row)}
                                    className="grid grid-cols-6 items-center px-6 py-4 bg-white dark:bg-[#262b33] rounded-xl shadow-sm text-center border border-gray-100 dark:border-gray-800 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition"
                                >
                                    <div className="text-[13px] font-medium text-gray-800 dark:text-gray-200 text-left">{row.date}</div>
                                    <div className="text-[13px] font-bold text-blue-600 dark:text-blue-400">{row.cause}</div>
                                    <div><span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${severityStyle}`}>{row.severity || 'Mild'}</span></div>
                                    <div className="text-[13px] font-medium text-gray-500 dark:text-gray-400">{row.reference}</div>
                                    <div className="text-[13px] font-medium text-gray-800 dark:text-gray-200">{row.completedBy}</div>
                                    <div className="flex justify-end">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setSelected(row); }}
                                            className="flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-700"
                                        >
                                            <Eye className="w-4 h-4" /> View
                                        </button>
                                    </div>
                                </div>
                                );
                            })
                        ) : (
                            <div className="py-12 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-[#262b33] rounded-xl border border-dashed border-gray-200 dark:border-gray-800">
                                {data.records.length > 0 ? 'No records match the current filters.' : 'No incident records found for this period.'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

const Chip = ({ label, onClear }) => (
    <span className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[12px] font-medium px-2.5 py-1 rounded-full">
        {label}
        <button onClick={onClear} className="hover:text-blue-900 dark:hover:text-blue-100"><X className="w-3 h-3" /></button>
    </span>
);

const IncidentDetailsModal = ({ record, onClose }) => {
    const severityStyle = severityStyles[record.severity] || 'bg-gray-100 text-gray-600 dark:bg-[#31373f] dark:text-gray-400';
    const yn = (v) => (v ? 'Yes' : 'No');
    const markers = Array.isArray(record.body_map_markers) ? record.body_map_markers : [];
    const numberedMarkers = markers.map((m, idx) => ({ ...m, globalIdx: idx }));

    return (
        <Modal isOpen onClose={onClose} hideHeader bodyClassName="overflow-y-auto" maxWidth="max-w-2xl" closeOnBackdrop>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-[#262b33]">
                    <div>
                        <h3 className="text-[17px] font-semibold text-gray-800 dark:text-gray-100">{record.cause}</h3>
                        <p className="text-[12px] text-gray-500 dark:text-gray-400">{record.reference}</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2c313a] text-gray-500">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${severityStyle}`}>{record.severity || 'Mild'}</span>
                        <span className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-[#2c313a] text-gray-600 dark:text-gray-300">
                            {CATEGORY_LABELS[record.category] || record.category}
                        </span>
                        <span className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-[#2c313a] text-gray-600 dark:text-gray-300">
                            {record.witnessed ? 'Witnessed' : 'Unwitnessed'}
                        </span>
                    </div>

                    <Section title="Overview">
                        <Detail label="Date" value={record.date} />
                        <Detail label="Time" value={record.time || '—'} />
                        <Detail label="Location" value={record.location} />
                        <Detail label="Reported By" value={record.reportedBy} />
                        <Detail label="Logged By" value={record.completedBy} />
                        <Detail label="Follow-up Date" value={record.followUpDate || '—'} />
                    </Section>

                    {record.description && (
                        <Section title="Description" single>
                            <p className="text-[13px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap col-span-2">{record.description}</p>
                        </Section>
                    )}

                    <Section title="Injury & Medical">
                        <Detail label="Hit Head" value={yn(record.hitHead)} />
                        <Detail label="Signs of Bleeding" value={yn(record.signsOfBleeding)} />
                        <Detail label="Signs of Pain" value={yn(record.signsOfPain)} />
                        <Detail label="Medication Given" value={yn(record.medicationAdministered)} />
                        <Detail label="Medical Attention" value={record.medicalAttention || '—'} />
                        <Detail label="Victims Involved" value={record.victimsInvolved || '—'} />
                    </Section>

                    <Section title="Wellbeing">
                        <Detail label="Emotion" value={record.emotion || '—'} />
                        <Detail label="Pain Score" value={`${record.painScore ?? 0} / 10`} />
                        {record.wellbeingNotes && <Detail label="Notes" value={record.wellbeingNotes} />}
                    </Section>

                    <Section title="Notifications">
                        <Detail label="Manager" value={yn(record.notifyManager)} />
                        <Detail label="Family" value={yn(record.notifyFamily)} />
                        <Detail label="GP" value={yn(record.notifyGp)} />
                        <Detail label="CQC" value={yn(record.notifyCqc)} />
                    </Section>

                    {markers.length > 0 && (
                        <Section title={`Body Map Markers (${markers.length})`} single>
                            <div className="col-span-2">
                                <div className="grid grid-cols-2 gap-2 border border-gray-100 dark:border-gray-700 rounded-xl bg-[#F8FAFC] dark:bg-[#1b1f26] p-3 mb-3">
                                    <div className="flex flex-col items-center">
                                        <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Front</span>
                                        <ReportBodyFigure side="front" markers={numberedMarkers} />
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Back</span>
                                        <ReportBodyFigure side="back" markers={numberedMarkers} />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    {numberedMarkers.map((m) => (
                                        <div key={m.globalIdx} className="flex items-center gap-2 text-[13px] text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-[#2c313a] rounded-lg px-3 py-2">
                                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                                                style={{ background: MARKER_SEVERITY_COLOR[m.severity] || '#22C55E' }}>
                                                {m.globalIdx + 1}
                                            </span>
                                            <span>{m.type || m.label || 'Mark'}</span>
                                            {m.severity && <span className="text-gray-400 dark:text-gray-500">· {m.severity}</span>}
                                            {(m.zoneLabel || m.label || m.zone) && <span className="text-gray-400 dark:text-gray-500">· {m.zoneLabel || m.label || m.zone}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Section>
                    )}
                </div>
        </Modal>
    );
};

const Section = ({ title, children, single }) => (
    <div>
        <h4 className="text-[13px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">{title}</h4>
        <div className={single ? 'grid grid-cols-2 gap-x-6 gap-y-3' : 'grid grid-cols-2 gap-x-6 gap-y-3'}>
            {children}
        </div>
    </div>
);

const Detail = ({ label, value }) => (
    <div>
        <p className="text-[12px] text-gray-400 dark:text-gray-500">{label}</p>
        <p className="text-[13px] font-medium text-gray-800 dark:text-gray-200 break-words">{value}</p>
    </div>
);

export default AccidentIncidentTab;
